import { PracticeSetup } from '@/features/flowsheets/PracticeSetup';
import { toast } from 'sonner';
import { Link } from '@tanstack/react-router';
import { Building2, Loader2, Save } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { CalculationProfile } from '@/features/imaging/CalculationProfile';
import { QuoSettings } from '@/features/quo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReportBranding } from '@/features/rendering';
import { TimezonePicker } from '@/components/ui/timezone-picker';
import { ModuleVisibility } from '../components/ModuleVisibility';
import { useOrgDateFormat, useOrgTimezone } from '@/lib/timezone';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { P, PermissionGuard, RequirePermission } from '@/features/permissions';
import { useOrganization, useUpdateOrganization } from '../api/organization-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function OrganizationPage() {
  return (
    <PermissionGuard permission={P.MANAGE_ORGANIZATION}>
      <OrganizationPageContent />
    </PermissionGuard>
  );
}

function OrganizationPageContent() {
  const setPageHeader = usePageHeaderSetter();
  const { data: org, isLoading } = useOrganization();
  const updateOrg = useUpdateOrganization();

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState<string | null>(null);
  const { isOrgConfigured, timeZone: resolvedZone } = useOrgTimezone();
  const { formatInstant } = useOrgDateFormat();

  useEffect(() => {
    if (org) {
      setName(org.name);
      setTimezone(org.timezone);
    }
  }, [org]);

  const isDirty = !!org && (name !== org.name || timezone !== org.timezone);

  const handleNameChange = (value: string) => {
    setName(value);
  };

  // Memoized so the page-header effect below (which hands this handler off to
  // an outer layout via context) can list it as a dependency and pick up a
  // fresh closure whenever anything it reads changes. Depends on
  // `updateOrg.mutateAsync` specifically, not the whole `updateOrg` object —
  // useMutation() returns a brand-new object every render (`{ ...result,
  // mutate, mutateAsync: result.mutate }`), so depending on it directly would
  // recreate this callback every render and (via the effect below) call
  // setPageHeader every render, which is a render loop. `mutateAsync` itself
  // (a bound MutationObserver method) is stable for the component's lifetime.
  const handleSave = useCallback(async () => {
    if (!isDirty || !name.trim()) return;
    try {
      await updateOrg.mutateAsync({
        name: name.trim(),
        ...(timezone && timezone !== org?.timezone ? { timezone } : {}),
      });
      toast.success('Organization updated');
    } catch {
      toast.error('Failed to update organization');
    }
  }, [isDirty, name, timezone, org?.timezone, updateOrg.mutateAsync]);

  useEffect(() => {
    setPageHeader({
      title: 'Organization Settings',
      actions: (
        <RequirePermission permission={P.MANAGE_ORGANIZATION}>
          <Button size="sm" disabled={!isDirty || updateOrg.isPending || !name.trim()} onClick={handleSave}>
            {updateOrg.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span className="hidden md:inline">Save</span>
          </Button>
        </RequirePermission>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, isDirty, updateOrg.isPending, name, handleSave]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-4 md:p-6">
        <div className="w-full max-w-2xl space-y-6">
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Organization not found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <div className="w-full max-w-2xl space-y-6">
        <RequirePermission permissions={['use_assistant', 'review_assistant_actions']} requireAll>
          <Card>
            <CardHeader>
              <CardTitle>Assistant workspace</CardTitle>
              <CardDescription>
                Review prepared work from faxes and laboratory reports. Open Practice settings in the workspace to
                manage automatic preparation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline">
                <Link to="/assistant">Open Assistant workspace</Link>
              </Button>
            </CardContent>
          </Card>
        </RequirePermission>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">General</CardTitle>
                <CardDescription>Manage your organization details</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                value={name}
                placeholder="Enter organization name"
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Organization ID</Label>
              <p className="rounded-md bg-muted px-3 py-2 font-mono text-sm">{org.id}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Created</Label>
                <p className="text-sm">{formatInstant(org.created_at, 'MMM d, yyyy')}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Last Updated</Label>
                <p className="text-sm">{formatInstant(org.updated_at, 'MMM d, yyyy')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Timezone</CardTitle>
            <CardDescription>
              Set the timezone your clinic works in, so everyone on your team sees the same dates and times — wherever
              they happen to be.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Label htmlFor="org-timezone">Your clinic&rsquo;s timezone</Label>
            <TimezonePicker id="org-timezone" value={timezone} disabled={updateOrg.isPending} onChange={setTimezone} />
            {!isOrgConfigured && !timezone && (
              <p className="text-sm text-muted-foreground">
                Not set yet — for now, everyone sees times in their own timezone. Yours is {resolvedZone}.{' '}
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm"
                  onClick={() => setTimezone(resolvedZone)}>
                  Use this
                </Button>
              </p>
            )}
          </CardContent>
        </Card>

        {/*
          Last, and after Timezone on purpose. The backend refuses to save a Quo
          connection until the organization has a timezone set -- an imported
          call's encounter_date is derived in the clinic's zone -- so the card
          that unblocks this one reads above it.
        */}
        <PracticeSetup />
        <ModuleVisibility />
        <CalculationProfile />
        <ReportBranding />
        <QuoSettings />
      </div>
    </div>
  );
}
